/**
 * Check if a given value equals to null
 *
 * @param {*}   object
 *
 * @returns {boolean}
 */
export function isNull(object)
{
    return object === null;
}

/**
 * Check if a given value is undefined
 *
 * @param {*} object
 *
 * @returns {boolean}
 */
export function isUndefined(object)
{
    // eslint-disable-next-line
    return typeof object === typeof void 0;
}

/**
 * Check if a given value is null or undefined
 *
 * @param {*}   object
 *
 * @returns {boolean}
 */
export function isNullOrUndefined(object)
{
    return isNull(object) || isUndefined(object);
}

/**
 * Check if a given value is null, undefined or empty string
 *
 * @param {*}   object
 *
 * @returns {boolean}
 */
export function isNullOrUndefinedOrEmpty(object)
{
    return isNullOrUndefined(object) || (object === "");
}

/**
 * Check if a given value is defined. This is a shorthand function for `!isNullOrUndefined(value)`
 * @param {*}   object
 *
 * @returns {boolean}
 */
export function isDefined(object)
{
    return !isNullOrUndefined(object);
}

/**
 * Check if a given value is defined. Otherwise return a default value
 *
 * @param {*}   input
 * @param {*}   defaultValue
 *
 * @returns {*}
 */
export function defaultValue(input, defaultValue)
{
    if (isNullOrUndefined(input))
    {
        return defaultValue;
    }

    return input;
}

export function orderArrayByKey(array, key, desc)
{
    return array.sort((valueA, valueB) =>
    {
        if (valueA[key] > valueB[key])
        {
            return 1;
        }
        if (valueA[key] < valueB[key])
        {
            return -1;
        }

        return 0;
    });
}
/**
 * Traverse the DOM upward starting from the input element until a DOM node with a vue component is found.
 * Returns the vue component, if found, else returns null.
 *
 * @param {Element}   element
 *
 * @returns {Vue}
 */
export function getContainingComponent(element)
{
    let parentComponent = null;

    while (element)
    {
        if (element.__vue__)
        {
            parentComponent = element.__vue__;
            break;
        }
        element = element.parentElement;
    }

    return parentComponent;
}

export function getDayOfYear(date = new Date())
{
    // Get the date components in Europe/Berlin timezone, so server (UTC)
    // and client (local) agree on what "today" is.
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Berlin",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).formatToParts(date);

    const year = Number(parts.find(part => part.type === "year").value);
    const month = Number(parts.find(part => part.type === "month").value);
    const day = Number(parts.find(part => part.type === "day").value);

    // Build a date using those components as if they were local.
    // Since startOfYear is built the same way, the timezone of the runtime cancels out.
    const localized = new Date(year, month - 1, day);
    const startOfYear = new Date(year, 0, 0);

    return Math.round((localized - startOfYear) / (1000 * 60 * 60 * 24));
}

export function getDayOfYearFromDateString(dateString)
{
    const datePart = dateString.split("T")[0];
    const dateParts = datePart.split("-").map(Number);

    if (dateParts.length !== 3 || dateParts.some(part => isNaN(part)))
    {
        return null;
    }

    return getDayOfYear(new Date(dateParts[0], dateParts[1] - 1, dateParts[2]));
}
